import { Table, type TableProps } from "antd"
import type { AnyObject } from "antd/es/_util/type"
import type { SelectProps } from "antd/es/select"
import type { TableRef } from "antd/es/table"
import React, { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../utils/cn.ts"
import { paginationItemRender, paginationShowTotal } from "./pagination-footer"
import "./styles.less"

interface TableComponentProps<RecordType = AnyObject>
  extends TableProps<RecordType> {
  withOutPagination?: boolean
  showSizeChanger?: boolean | SelectProps
  hoverMarker?: any
}

function TableComponent<RecordType = AnyObject>({
  loading,
  dataSource,
  pagination,
  withOutPagination,
  showSizeChanger,
  rowKey,
  onScroll,
  hoverMarker,
  className,
  ...props
}: TableComponentProps<RecordType>) {
  const { t } = useTranslation()
  const tableRef = useRef<TableRef>(null)

  useEffect(() => {
    if (!onScroll) return

    const tableBody =
      tableRef.current?.nativeElement?.querySelector<HTMLElement>(
        ".ant-table-body"
      )
    if (!tableBody) return

    tableBody.addEventListener("scroll", onScroll as any)
    return () => tableBody.removeEventListener("scroll", onScroll as any)
    // Handing the handler alone is enough: whether there is another page to
    // fetch is the caller's `hasMore`, and a listener gated on a second prop
    // saying the same thing is how a table ends up silently stuck on page one.
    //
    // `onScroll` belongs in the deps because a listener left bound answers with
    // the props it closed over; `dataSource` because the body element does not
    // exist to bind to until the first rows land.
  }, [onScroll, dataSource])

  useEffect(() => {
    if (hoverMarker && tableRef?.current) {
      tableRef?.current?.scrollTo({ key: hoverMarker })
    }
  }, [hoverMarker])

  return (
    <Table<any>
      ref={tableRef}
      className={cn("custom-table", className)}
      rowKey={(record) => record?.[(rowKey as any) || "id"]}
      loading={loading}
      dataSource={dataSource}
      showSorterTooltip={false}
      rowHoverable={false}
      scroll={{ x: "max-content" }}
      pagination={
        !dataSource?.length || withOutPagination
          ? false
          : {
              position: ["none", "bottomLeft"],
              showLessItems: true,
              showSizeChanger: showSizeChanger,
              //pageSizeOptions: [1,2,3],
              itemRender: paginationItemRender(
                t,
                (pagination as any)?.pageSize >= (pagination as any)?.total
              ),
              showTotal: paginationShowTotal,
              ...pagination
            }
      }
      {...props}
    />
  )
}

const MemoizedTable = React.memo(TableComponent) as typeof TableComponent

export default MemoizedTable
